import Antd from 'ant-design-vue';
import ElementUI from 'element-ui';
import 'element-ui/lib/theme-chalk/index.css';
import moment from 'moment';
import Vue from 'vue';
import VueLazyload from 'vue-lazyload';
import Print from 'vue-print-nb';
import VxeUIDesign from 'vxe-design';
import 'vxe-design/lib/style.css';
import VxeUIBase from 'vxe-pc-ui';
import 'vxe-pc-ui/es/style.css';
import VxeUITable from 'vxe-table';
import 'vxe-table/es/style.css';
import { AppPreBootstrap } from './AppPreBootstrap';
import { setupComponents } from './components';
import root from './root.vue';
import { rootRouting } from './router';
import { appSessionService, permissionService } from './shared/abp';
import { setupIcon } from './shared/antd/import-antd-icon';
import { authRouteGuard } from './shared/auth';
import { layoutService, menuService, reuseTabService } from './shared/common';
import UserNotificationHelper from './shared/helpers/UserNotificationHelper';
import { localizationService } from './shared/i18n';
import { rootStore } from './shared/store';
import { preloaderFinished } from './shared/utils';
import './styles/index.less';

declare global {
    interface Window {
        __LEANASST_DEBUG__?: {
            app: Vue;
            router: typeof rootRouting;
            navigate: (path: string) => Promise<void>;
            sample: () => {
                route: string;
                usedJSHeapSize: number | null;
                totalJSHeapSize: number | null;
                jsHeapSizeLimit: number | null;
                domNodes: number;
                windowResizeListeners: number | null;
                windowEventListeners: number | null;
                documentEventListeners: number | null;
                intervalCount: number | null;
                timeoutCount: number | null;
                echartsDomCount: number;
                canvasCount: number;
                timestamp: string;
            };
        };
        __LEANASST_RESOURCE_TRACKER__?: {
            windowEventListeners: number;
            documentEventListeners: number;
            windowResizeListeners: number;
            intervalCount: number;
            timeoutCount: number;
            patched: boolean;
        };
    }
}

Vue.use(Print);
Vue.use(ElementUI);
Vue.config.productionTip = false;
Vue.use(Antd);

// antd 组件内置文案(日期月份/星期等)按系统语言动态提供,供各页面 locale-provider 使用
import zh_CN from 'ant-design-vue/lib/locale-provider/zh_CN';
import en_US from 'ant-design-vue/lib/locale-provider/en_US';
Vue.mixin({
    computed: {
        antdLocale() {
            const abp = (window as any).abp;
            const lang = abp && abp.localization && abp.localization.currentLanguage
                ? abp.localization.currentLanguage.name : 'zh-Hans';
            return lang && lang.indexOf('zh') === 0 ? zh_CN : en_US;
        },
    },
});
Vue.use(VueLazyload, {
    ispatchEvent: true,
});

Vue.use(VxeUIBase);
Vue.use(VxeUITable);
Vue.use(VxeUIDesign);

Vue.filter('moment', (value, formatString) => {
    formatString = formatString || 'YYYY-MM-DDTHH:mm:ss';
    return moment(value).format(formatString);
});

moment.prototype.toISOString = function() {
    return moment(this).format('YYYY-MM-DD HH:mm:ss');
};

function installDevResourceTracker() {
    if (process.env.NODE_ENV === 'production') {
        return;
    }

    if (window.__LEANASST_RESOURCE_TRACKER__?.patched) {
        return;
    }

    const tracker = {
        windowEventListeners: 0,
        documentEventListeners: 0,
        windowResizeListeners: 0,
        intervalCount: 0,
        timeoutCount: 0,
        patched: true,
    };

    const eventTargetPrototype = EventTarget.prototype as EventTarget & {
        __leanAsstAddEventListener__?: EventTarget['addEventListener'];
        __leanAsstRemoveEventListener__?: EventTarget['removeEventListener'];
    };

    if (!eventTargetPrototype.__leanAsstAddEventListener__) {
        eventTargetPrototype.__leanAsstAddEventListener__ = eventTargetPrototype.addEventListener;
        eventTargetPrototype.__leanAsstRemoveEventListener__ = eventTargetPrototype.removeEventListener;

        eventTargetPrototype.addEventListener = function(type, listener, options) {
            if (this === window) {
                tracker.windowEventListeners += 1;
                if (type === 'resize') {
                    tracker.windowResizeListeners += 1;
                }
            }

            if (this === document) {
                tracker.documentEventListeners += 1;
            }

            return eventTargetPrototype.__leanAsstAddEventListener__.call(this, type, listener, options);
        };

        eventTargetPrototype.removeEventListener = function(type, listener, options) {
            if (this === window) {
                tracker.windowEventListeners = Math.max(0, tracker.windowEventListeners - 1);
                if (type === 'resize') {
                    tracker.windowResizeListeners = Math.max(0, tracker.windowResizeListeners - 1);
                }
            }

            if (this === document) {
                tracker.documentEventListeners = Math.max(0, tracker.documentEventListeners - 1);
            }

            return eventTargetPrototype.__leanAsstRemoveEventListener__.call(this, type, listener, options);
        };
    }

    const originalSetInterval = window.setInterval.bind(window);
    const originalClearInterval = window.clearInterval.bind(window);
    const originalSetTimeout = window.setTimeout.bind(window);
    const originalClearTimeout = window.clearTimeout.bind(window);
    const intervalIds = new Set<number>();
    const timeoutIds = new Set<number>();

    window.setInterval = ((handler: TimerHandler, timeout?: number, ...args: any[]) => {
        const id = originalSetInterval(handler, timeout, ...args);
        intervalIds.add(id);
        tracker.intervalCount = intervalIds.size;
        return id;
    }) as typeof window.setInterval;

    window.clearInterval = ((id?: number) => {
        if (typeof id === 'number') {
            intervalIds.delete(id);
            tracker.intervalCount = intervalIds.size;
        }
        return originalClearInterval(id);
    }) as typeof window.clearInterval;

    window.setTimeout = ((handler: TimerHandler, timeout?: number, ...args: any[]) => {
        const id = originalSetTimeout(handler, timeout, ...args);
        timeoutIds.add(id);
        tracker.timeoutCount = timeoutIds.size;
        return id;
    }) as typeof window.setTimeout;

    window.clearTimeout = ((id?: number) => {
        if (typeof id === 'number') {
            timeoutIds.delete(id);
            tracker.timeoutCount = timeoutIds.size;
        }
        return originalClearTimeout(id);
    }) as typeof window.clearTimeout;

    window.__LEANASST_RESOURCE_TRACKER__ = tracker;
}

installDevResourceTracker();

// vxe-table/vxe-pc-ui 内置文案(列设置等)跟随系统语言
import { VxeUI } from 'vxe-pc-ui';
import vxeZhCN from 'vxe-pc-ui/lib/language/zh-CN';
import vxeEnUS from 'vxe-pc-ui/lib/language/en-US';
VxeUI.setI18n('zh-CN', vxeZhCN);
VxeUI.setI18n('en-US', vxeEnUS);

AppPreBootstrap.run(() => {
    appSessionService.init().then(() => {
        // vxe 组件语言跟随系统语言
        const vxeLang = (window.abp && abp.localization && abp.localization.currentLanguage
            ? abp.localization.currentLanguage.name : 'zh-Hans').indexOf('zh') === 0 ? 'zh-CN' : 'en-US';
        VxeUI.setLanguage(vxeLang);
        setupIcon();
        setupComponents();

        authRouteGuard.init(rootRouting);
        reuseTabService.init(rootRouting, localizationService);
        menuService.initService(localizationService, permissionService);

        UserNotificationHelper.init();

        layoutService.data.reuseTab = true;

        const app = new Vue({
            router: rootRouting,
            store: rootStore,
            render: (h) => h(root),
            mounted() {
                preloaderFinished();
            },
        }).$mount('#app');

        if (process.env.NODE_ENV !== 'production') {
            const browserPerformance = performance as Performance & {
                memory?: {
                    usedJSHeapSize: number;
                    totalJSHeapSize: number;
                    jsHeapSizeLimit: number;
                };
            };

            window.__LEANASST_DEBUG__ = {
                app,
                router: rootRouting,
                navigate: async (path: string) => {
                    if (rootRouting.currentRoute.fullPath === path) {
                        return;
                    }

                    try {
                        await rootRouting.push(path);
                    } catch (error) {
                        // Ignore duplicated navigation and keep the probe moving.
                    }
                },
                sample: () => ({
                    route: rootRouting.currentRoute.fullPath,
                    usedJSHeapSize: browserPerformance.memory?.usedJSHeapSize ?? null,
                    totalJSHeapSize: browserPerformance.memory?.totalJSHeapSize ?? null,
                    jsHeapSizeLimit: browserPerformance.memory?.jsHeapSizeLimit ?? null,
                    domNodes: document.querySelectorAll('*').length,
                    windowResizeListeners: window.__LEANASST_RESOURCE_TRACKER__?.windowResizeListeners ?? null,
                    windowEventListeners: window.__LEANASST_RESOURCE_TRACKER__?.windowEventListeners ?? null,
                    documentEventListeners: window.__LEANASST_RESOURCE_TRACKER__?.documentEventListeners ?? null,
                    intervalCount: window.__LEANASST_RESOURCE_TRACKER__?.intervalCount ?? null,
                    timeoutCount: window.__LEANASST_RESOURCE_TRACKER__?.timeoutCount ?? null,
                    echartsDomCount: document.querySelectorAll('[_echarts_instance_]').length,
                    canvasCount: document.querySelectorAll('canvas').length,
                    timestamp: new Date().toISOString(),
                }),
            };
        }
    });
});
